/**
 * sceneLanding — NSPP's hero.
 *
 * Design philosophy: SIMPLICITY OVER NARRATIVE.
 *
 * The previous versions tried to fit a full marketing pitch into one
 * viewport (massive wordmark + eyebrow + sub + conviction + CTAs +
 * right chips + proof strip + nav) and on small viewports the result
 * was clipped, cramped, and ugly. This rewrite drops everything that
 * wasn't working and replaces it with one strong hero:
 *
 *   - LEFT HALF (50% viewport): the type. A real headline.
 *     "NSPP" set in serif, large but fitted to its half. Below it:
 *     "the GPU-rendered UI engine for designers who ship motion."
 *
 *   - RIGHT HALF (50% viewport): a real product shot — the Maison Verre
 *     bottle from /static/products/maison-verre/no2/view-hero.svg,
 *     rendered through the engine as an image node. The bottle IS the
 *     proof: luxury brands would pay $50K-$200K for a product page like
 *     the one in /maison-verre. The landing doesn't say that — it shows
 *     the bottle, and the "BUY IT" button below it routes to /maison-verre.
 *
 *   - BOTTOM: a single line of proof — "60 FPS · SDF rects · motion blur ·
 *     per-instance filter chains · backdrop blur · mix-blend-mode" —
 *     terse and ends the page.
 *
 * Layout adapts: on narrow viewports the bottle drops below the type.
 *
 * No clicking required — the entire scene is information.
 */

import {
  type Scene,
  createScene,
  makeRect,
  makeText,
  makeImage,
  addNode,
} from "../sceneGraph";

export function sceneLanding(w: number, h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();
  const isMobile = w < 720;
  const padX = isMobile ? 24 : 56;
  const padY = isMobile ? 28 : 44;

  // ============================================================
  // 0. BACKGROUND — vertical gradient
  // ============================================================
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: { r: 0.025, g: 0.025, b: 0.06, a: 1 },
    gradientEnd: { r: 0.05, g: 0.04, b: 0.10, a: 1 },
    gradientAngle: Math.PI / 2,
  })); z++;

  // Single warm orb BEHIND the wordmark — gives the page warmth without
  // dominating. Positioned below the wordmark so it doesn't clash with
  // the bottle on the right.
  const orb = makeRect({
    x: w * 0.08, y: h * 0.10, w: 320, h: 320,
    radius: 160,
    fill: { r: 0.96, g: 0.55, b: 0.20, a: 0.10 },
    filter: { blur: 0, brightness: 1.0, contrast: 1, saturate: 1.3, hueRotate: 0, invert: 0 },
  });
  addNode(scene, orb); z++;
  orb.update = (n) => {
    n.x = w * 0.08 + Math.sin(T() / 4500) * 24;
    n.y = h * 0.10 + Math.cos(T() / 5000) * 16;
  };

  // ============================================================
  // 1. NAV — minimal
  // ============================================================
  addNode(scene, makeRect({
    x: padX, y: padY - 12, w: 10, h: 10,
    radius: 2,
    fill: { r: 0.96, g: 0.55, b: 0.20, a: 1 },
  })); z++;
  addNode(scene, makeText({
    x: padX + 16, y: padY - 14, w: 80, h: 14,
    text: "NSPP",
    fontSize: 12,
    fontWeight: 700,
    textColor: { r: 1, g: 1, b: 1, a: 0.95 },
    letterSpacing: 0.32,
    fontFamily: "'Times New Roman', Georgia, serif",
  })); z++;

  // ============================================================
  // 2. LEFT SIDE — type stack
  // ============================================================
  // On desktop this is the LEFT HALF. On mobile it spans full width
  // above the bottle.
  const leftW = isMobile ? w - padX * 2 : Math.min(640, w * 0.5 - padX * 2);
  const leftX = padX;
  let leftY = padY + 50;

  // Eyebrow
  addNode(scene, makeText({
    x: leftX, y: leftY, w: leftW, h: 14,
    text: "A WEBGL2 UI RENDERER · MMXXVI",
    fontSize: 10,
    fontWeight: 700,
    textColor: { r: 0.96, g: 0.55, b: 0.20, a: 0.95 },
    letterSpacing: 0.40,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;
  leftY += 32;

  // Massive wordmark — fitted to leftW
  const wordmarkFontSize = isMobile
    ? Math.min(w - padX * 2, 140)
    : Math.min(leftW, 220);
  const wordmarkH = wordmarkFontSize * 1.0;
  addNode(scene, makeText({
    x: leftX, y: leftY, w: leftW, h: wordmarkH,
    text: "NSPP",
    fontSize: wordmarkFontSize,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    letterSpacing: 0.02,
    lineHeight: 1.0,
    fontFamily: "'Times New Roman', Georgia, serif",
  })); z++;
  leftY += wordmarkH + 24;

  // Subline: what it is, in plain serif
  addNode(scene, makeText({
    x: leftX, y: leftY, w: leftW, h: 60,
    text: isMobile
      ? "The GPU-rendered\nUI engine for designers\nwho ship motion."
      : "The GPU-rendered UI engine\nfor designers who ship motion.",
    fontSize: isMobile ? 22 : 28,
    fontWeight: 400,
    textColor: { r: 1, g: 1, b: 1, a: 0.85 },
    lineHeight: 1.15,
    fontFamily: "Georgia, 'Times New Roman', serif",
    letterSpacing: 0.005,
  })); z++;
  leftY += isMobile ? 90 : 80;

  // ONE CTA — gold pill "TRY A DEMO" → routes to /advanced
  const ctaW = 200;
  const ctaH = 48;
  addNode(scene, makeRect({
    x: leftX, y: leftY, w: ctaW, h: ctaH,
    radius: 24,
    fill: { r: 0.96, g: 0.55, b: 0.20, a: 1 },
    clickScene: "advanced",
  })); z++;
  addNode(scene, makeText({
    x: leftX, y: leftY, w: ctaW, h: ctaH,
    text: "TRY A DEMO",
    fontSize: 12,
    fontWeight: 700,
    textColor: { r: 0.06, g: 0.05, b: 0.10, a: 1 },
    textAlign: "center",
    letterSpacing: 0.24,
    fontFamily: "system-ui, -apple-system, sans-serif",
    clickScene: "advanced",
  })); z++;

  // ============================================================
  // 3. RIGHT SIDE — the bottle (Maison Verre, real SVG asset)
  // ============================================================
  // The SVG's intrinsic aspect is 1440:2400 (very tall). On a 960×474
  // viewport we have ~470px of right-half width available. Filling the
  // right half vertically with the bottle (cropping top/bottom of the
  // SVG) gives the most striking presentation.
  let bottleW: number, bottleH: number, bottleX: number, bottleY: number;
  if (isMobile) {
    // Mobile: drop the bottle entirely to save vertical space. The
    // proof line + tiny CTA is enough.
    bottleW = 0; bottleH = 0; bottleX = 0; bottleY = 0;
  } else {
    // Right half = 50% of viewport width, full height (with padding).
    bottleW = w * 0.40;
    bottleX = w - padX - bottleW;
    bottleY = padY + 28;
    bottleH = h - padY * 2 - 64; // leave room for proof + caption
  }
  if (bottleW > 0) {
    // The bottle itself. Image node, real asset.
    addNode(scene, makeImage({
      x: bottleX, y: bottleY, w: bottleW, h: bottleH,
      imageUrl: "/products/maison-verre/no2/view-hero.svg",
    })); z++;

    // Tap-the-bottle → routes to /maison-verre (the full PDP scene)
    // Done via a transparent rect overlay on top of the bottle.
    addNode(scene, makeRect({
      x: bottleX, y: bottleY, w: bottleW, h: bottleH,
      radius: 0,
      fill: { r: 1, g: 1, b: 1, a: 0 },
      clickScene: "maison-verre",
      tagName: "landing-bottle",
    })); z++;

    // "Maison Verre · Nº 01" small caption near the bottle
    addNode(scene, makeText({
      x: bottleX, y: bottleY + bottleH + 8, w: bottleW, h: 12,
      text: "MAISON VERRE · Nº 01",
      fontSize: 9,
      fontWeight: 700,
      textColor: { r: 1, g: 1, b: 1, a: 0.55 },
      textAlign: "center",
      letterSpacing: 0.36,
      fontFamily: "system-ui, -apple-system, sans-serif",
    })); z++;
  }

  // ============================================================
  // 4. PROOF LINE — bottom of viewport, single line
  // ============================================================
  const proofY = h - 36;
  addNode(scene, makeRect({
    x: padX, y: proofY - 14, w: w - padX * 2, h: 1,
    fill: { r: 1, g: 1, b: 1, a: 0.10 },
  })); z++;
  // Shorter so it doesn't overflow on small viewports.
  addNode(scene, makeText({
    x: padX, y: proofY, w: w - padX * 2, h: 14,
    text: isMobile
      ? "60 FPS · SDF · motion blur · filters · glass · blend"
      : "60 FPS  ·  SDF rects  ·  motion blur  ·  filter chains  ·  glass  ·  blend modes",
    fontSize: 10,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.45 },
    letterSpacing: 0.04,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  return scene;
}
